// Seniority badge component. Reads `provider_profiles.tier`. The trust signal lives
// on `provider_profiles.trust_status` (separate concept) — see docs/glossary.md.
// IMPORTANT: this component renders nothing unless `trustStatus === 'verified'`.
// Seniority MUST NOT be visible on unverified or pending profiles.
import { ShieldCheck, Sparkles, Gem } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Legacy values 'unverified' | 'verified' are accepted in the prop signature for
// backward compatibility with old call sites and stale row data, but they are
// always treated as "no badge". The DB CHECK now only allows silver/gold/platinum.
export type ProviderTier =
  | "silver"
  | "gold"
  | "platinum"
  | "unverified"
  | "verified";

type ActiveTier = "silver" | "gold" | "platinum";

const STYLES: Record<
  ActiveTier,
  { bg: string; fg: string; Icon: typeof ShieldCheck; labelKey: string; tooltipKey: string }
> = {
  silver: {
    bg: "hsl(0 0% 90%)",
    fg: "hsl(0 0% 25%)",
    Icon: ShieldCheck,
    labelKey: "tier.silver.label",
    tooltipKey: "tier.silver.tooltip",
  },
  gold: {
    bg: "hsl(45 90% 90%)",
    fg: "hsl(36 70% 30%)",
    Icon: Sparkles,
    labelKey: "tier.gold.label",
    tooltipKey: "tier.gold.tooltip",
  },
  platinum: {
    // Cool platinum gradient inline; subtle shimmer via background-size animation in CSS.
    bg: "linear-gradient(135deg, hsl(220 14% 88%) 0%, hsl(210 18% 70%) 50%, hsl(220 14% 88%) 100%)",
    fg: "hsl(220 25% 18%)",
    Icon: Gem,
    labelKey: "tier.platinum.label",
    tooltipKey: "tier.platinum.tooltip",
  },
};

function isActiveTier(t: ProviderTier | null | undefined): t is ActiveTier {
  return t === "silver" || t === "gold" || t === "platinum";
}

export function TierBadge({
  tier,
  trustStatus,
  size = 11,
  className,
}: {
  tier: ProviderTier | null | undefined;
  /** Required: seniority is hidden unless the provider is verified. */
  trustStatus?: "not_verified" | "pending" | "verified" | null;
  size?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  // Trust gate. If caller didn't pass it (legacy site), default to hidden so we
  // never accidentally surface seniority for unverified providers.
  if (trustStatus !== "verified") return null;
  if (!isActiveTier(tier)) return null;
  const s = STYLES[tier];
  const label = t(s.labelKey);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            aria-label={label}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[10px] font-bold uppercase tracking-wider ${className ?? ""}`}
            style={{ background: s.bg, color: s.fg }}
          >
            <s.Icon size={size} aria-hidden />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-[240px] text-xs">{t(s.tooltipKey)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}